import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Calendar } from 'primereact/calendar';

import './ShiftTimePicker.scss';

/**
 * Convert a facility-timezone moment into a browser-local Date whose wall-clock
 * equals the facility-local time, so the PrimeReact Calendar (which renders Dates
 * in browser-local time) displays the facility-local time. Returns null when absent.
 */
const toPickerDate = (timeMoment) => {
	if (!timeMoment) {
		return null;
	}
	return new Date(
		timeMoment.year(),
		timeMoment.month(),
		timeMoment.date(),
		timeMoment.hour(),
		timeMoment.minute(),
		0,
		0
	);
};

/**
 * Map an hour/minute onto the shift's calendar day, rolling over to the next day
 * when the resulting time falls before the shift start so overnight shifts (e.g.
 * 7pm-7am) order correctly. Mirrors the backend resolveSegmentTime rule.
 */
const applyShiftTime = (hour, minute, shiftStart) => {
	const candidate = shiftStart.clone().set({ hour, minute, second: 0, millisecond: 0 });
	if (candidate.isBefore(shiftStart)) {
		candidate.add(1, 'day');
	}
	return candidate;
};

const isDigit = (char) => char >= '0' && char <= '9';

const isSpace = (char) =>
	char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f' || char === '\v';

/**
 * Tokenize a trimmed, lowercased time string into its raw parts without regex.
 * Walks a cursor through: 1-2 digit hour, optional ":MM" (exactly 2 digits),
 * optional spaces, optional meridiem ("a"/"am"/"p"/"pm"). Returns null if the
 * whole string isn't consumed by that grammar (range checks happen in the caller).
 */
const parseTimeTokens = (text) => {
	let index = 0;

	let hourText = '';
	while (index < text.length && isDigit(text[index]) && hourText.length < 2) {
		hourText += text[index];
		index += 1;
	}
	if (!hourText) {
		return null;
	}

	let minuteText = '';
	if (text[index] === ':') {
		index += 1;
		while (index < text.length && isDigit(text[index]) && minuteText.length < 2) {
			minuteText += text[index];
			index += 1;
		}
		if (minuteText.length !== 2) {
			return null;
		}
	}

	while (index < text.length && isSpace(text[index])) {
		index += 1;
	}

	let meridiem = null;
	if (index < text.length) {
		const rest = text.slice(index);
		if (rest === 'a' || rest === 'am') {
			meridiem = 'a';
		} else if (rest === 'p' || rest === 'pm') {
			meridiem = 'p';
		} else {
			return null;
		}
	}

	return { hour: parseInt(hourText, 10), minute: minuteText ? parseInt(minuteText, 10) : 0, meridiem };
};

/**
 * Parse a loosely-typed time string into a 24-hour { hour, minute }.
 *
 * Accepts (case- and whitespace-insensitive): "1pm" -> 13:00, "1am" -> 01:00,
 * "1:23am" -> 01:23, "1:23 pm" -> 13:23, "13"/"13:00" -> 13:00 (24h),
 * "1:23"/"1"/"7"/"11" (bare 1-11, no am/pm) -> inferred against the shift, "12" -> noon.
 * Returns null for unparseable input, out-of-range hours, or minutes > 59.
 *
 * For an ambiguous bare hour 1-11 with no am/pm, the am (h) and pm (h+12)
 * candidates are each mapped onto the shift's calendar day (rolling overnight).
 * The one that lands inside the shift window [shiftStart, shiftEnd] wins; when
 * both (or neither) fit, the earliest is used.
 */
const parseFlexibleTime = (rawText, shiftStart, shiftEnd) => {
	const text = (rawText || '').trim().toLowerCase();
	const tokens = parseTimeTokens(text);
	if (!tokens) {
		return null;
	}
	const { hour, minute, meridiem } = tokens;
	if (minute > 59) {
		return null;
	}

	if (meridiem) {
		if (hour < 1 || hour > 12) {
			return null;
		}
		if (meridiem === 'a') {
			return { hour: hour === 12 ? 0 : hour, minute };
		}
		return { hour: hour === 12 ? 12 : hour + 12, minute };
	}

	if (hour > 23) {
		return null;
	}
	if (hour === 0 || hour === 12 || hour >= 13) {
		return { hour, minute };
	}

	const options = [
		{ hour, moment: applyShiftTime(hour, minute, shiftStart) },
		{ hour: hour + 12, moment: applyShiftTime(hour + 12, minute, shiftStart) }
	];
	const withinShift = options.filter((option) => option.moment.isSameOrBefore(shiftEnd));
	const pool = withinShift.length ? withinShift : options;
	pool.sort((a, b) => a.moment.valueOf() - b.moment.valueOf());
	return { hour: pool[0].hour, minute };
};

/**
 * Time-of-day field bound to a shift. Built on PrimeReact's Calendar, it supports
 * loose manual entry: the field holds raw text while typing (so characters can be
 * edited/deleted one at a time) and parses it on blur or Enter, resolving the
 * wall-clock time onto the shift's day (with overnight rollover). `onChange` fires
 * with a facility-timezone moment for a resolved time, null when the field is
 * cleared, and `onInvalid` fires when the text can't be parsed. Enter commits and
 * closes the panel; selecting from the dropdown panel resolves the same way.
 *
 * @param {object} props
 * @param {import('moment').Moment | null} props.value - Facility-tz moment to display, or null.
 * @param {import('moment').Moment} props.shiftStart - Shift start (for resolution + am/pm inference).
 * @param {import('moment').Moment} props.shiftEnd - Shift end (for am/pm inference).
 * @param {boolean} [props.disabled] - Disables the field.
 * @param {(value: (import('moment').Moment | null)) => void} props.onChange - Resolved time, or null when cleared.
 * @param {() => void} props.onInvalid - Called when the typed text can't be parsed.
 * @param {string} [props.className] - Class applied to the Calendar (e.g. an invalid-state highlight).
 * @param {string} [props.panelClassName] - Class applied to the dropdown panel.
 * @returns {JSX.Element}
 */
const ShiftTimePicker = ({ value, shiftStart, shiftEnd, disabled, onChange, onInvalid, className, panelClassName }) => {
	const calendarRef = useRef(null);
	// Set when Enter triggers a programmatic blur, so the ensuing blur doesn't
	// re-parse the value Enter already committed.
	const skipBlurResolveRef = useRef(false);

	const resolveText = useCallback(
		(rawText) => {
			const trimmed = (rawText || '').trim();
			if (!trimmed) {
				const now = new Date();
				onChange(applyShiftTime(now.getHours(), now.getMinutes(), shiftStart));
				return;
			}
			const parsed = parseFlexibleTime(trimmed, shiftStart, shiftEnd);
			if (!parsed) {
				onInvalid();
				return;
			}
			onChange(applyShiftTime(parsed.hour, parsed.minute, shiftStart));
		},
		[onChange, onInvalid, shiftStart, shiftEnd]
	);

	// PrimeReact Calendar hardcodes its input onKeyDown and only forwards onBlur,
	// so bind Enter directly on the underlying input to commit and close the panel.
	useEffect(() => {
		const input = calendarRef.current && calendarRef.current.getInput();
		if (!input) {
			return undefined;
		}
		const handleEnter = (event) => {
			if (event.key === 'Enter') {
				resolveText(event.target.value);
				if (calendarRef.current) {
					calendarRef.current.hide();
				}
				skipBlurResolveRef.current = true;
				event.target.blur();
			}
		};
		input.addEventListener('keydown', handleEnter);
		return () => input.removeEventListener('keydown', handleEnter);
	}, [resolveText]);

	const handleChange = (event) => {
		if (event.value instanceof Date) {
			onChange(applyShiftTime(event.value.getHours(), event.value.getMinutes(), shiftStart));
		}
	};
	const handleBlur = (event) => {
		if (skipBlurResolveRef.current) {
			skipBlurResolveRef.current = false;
			return;
		}
		resolveText(event.target.value);
	};
	const calendarClassName = ['shift-time-picker', className].filter(Boolean).join(' ');
	// Keep a stable Date identity so PrimeReact's value-change effect doesn't fire on
	// every render and overwrite invalid text the user typed (the error-state re-render
	// would otherwise reset the input to the last valid time).
	const pickerValue = useMemo(() => toPickerDate(value), [value]);

	return (
		<Calendar
			ref={calendarRef}
			timeOnly
			hourFormat="12"
			stepMinute={1}
			keepInvalid
			value={pickerValue}
			disabled={disabled}
			onChange={handleChange}
			onBlur={handleBlur}
			className={calendarClassName}
			panelClassName={panelClassName}
		/>
	);
};

export default ShiftTimePicker;
